import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import MFAVerification from '../../components/MFAVerification';
import zeroKnowledgePasscodeService from '../../services/ZeroKnowledgePasscodeService';
import passcodeManager from '../../services/PasscodeManager';
import './styles.css';

export default function PasscodeEntry({ isPrivacyFolder = false }) {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaData, setMfaData] = useState(null);
  const passcodeRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { member } = location.state || { member: null };
  
  useEffect(() => {
    if (!member) {
      navigate('/member-selection');
      return;
    }
    
    // Entrance animation
    gsap.from(containerRef.current, {
      y: 20,
      duration: 0.8,
      ease: "power3.out"
    });
    
    // Focus passcode input
    passcodeRef.current?.focus();
  }, [member, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';
      const endpoint = isPrivacyFolder
        ? `${API_BASE_URL}/api/members/${member._id || member.id}/verify-privacy-folder`
        : `${API_BASE_URL}/api/members/${member._id || member.id}/verify`;
      
      // Prepare passcode for server (handles both legacy and zero-knowledge modes)
      const passcodeForServer = await zeroKnowledgePasscodeService.preparePasscodeForServer(passcode, member);
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: passcodeForServer })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Invalid vault key. Please try again.');
        setPasscode('');
        // Error animation
        gsap.to(passcodeRef.current, {
          x: [-10, 10, -10, 10, 0],
          duration: 0.4,
          ease: "power2.inOut"
        });
        setLoading(false);
        return;
      }
      
      // Check if MFA is required
      if (data.requiresMFA) {
        // Store passcode directly in sessionStorage BEFORE showing MFA
        const targetId = String(member._id || member.id || member.memberId);
        console.log('🔐 PasscodeEntry: MFA required, storing passcode in sessionStorage for:', targetId);
        sessionStorage.setItem(`eternavaults_passcode_${targetId}`, passcode);
        sessionStorage.setItem(`eternavaults_passcode_timestamp_${targetId}`, Date.now().toString());
        
        setMfaData({
          memberId: data.member._id,
          deviceId: data.deviceId,
          deviceName: data.deviceName
        });
        setShowMFA(true);
        setLoading(false);
        return;
      }
      
      // Check if this is a temporary passcode (first-time user)
      if (data.isTemporaryPasscode) {
        // Redirect to set permanent passcode
        navigate('/reset-passcode', { 
          state: { 
            memberId: member._id || member.id, 
            isTemporary: true 
          } 
        });
        return;
      }
      
      // Store current user in session storage with timestamp
      sessionStorage.setItem('currentMember', JSON.stringify(member));
      sessionStorage.setItem('passcodeVerifiedAt', Date.now().toString());
      
      // Store passcode directly in sessionStorage for encryption operations
      const targetId = String(member._id || member.id || member.memberId);
      console.log('🔐 PasscodeEntry: Storing passcode in sessionStorage for:', targetId);
      sessionStorage.setItem(`eternavaults_passcode_${targetId}`, passcode);
      sessionStorage.setItem(`eternavaults_passcode_timestamp_${targetId}`, Date.now().toString());
      console.log('✅ PasscodeEntry: Passcode stored. Verifying...');
      const verifyPasscode = sessionStorage.getItem(`eternavaults_passcode_${targetId}`);
      console.log('🔍 PasscodeEntry: Verification - passcode found?', !!verifyPasscode);
      // Success animation before redirect
      gsap.to(containerRef.current, {
        scale: 1.05,
        duration: 0.3,
        ease: "power3.out",
        onComplete: () => {
          const targetId = member._id || member.id || member.memberId;
          navigate(`/dashboard/${targetId}`);
        }
      });
    } catch (err) {
      setError('Server error. Please try again.');
      setPasscode('');
      setLoading(false);
    }
  };

  
  const handleForgotPasscode = () => {
    navigate('/reset-passcode', { state: { memberId: member.id } });
  };
  
  const handleBack = () => {
    navigate('/member-selection');
  };
  
  const handleMFAVerified = () => {
    setShowMFA(false);
    setMfaData(null);
    
    // Store current user in session storage with timestamp
    sessionStorage.setItem('currentMember', JSON.stringify(member));
    sessionStorage.setItem('passcodeVerifiedAt', Date.now().toString());
    
    // Get passcode from sessionStorage (stored before MFA) or from state
    const targetId = String(member._id || member.id || member.memberId);
    const storedPasscode = sessionStorage.getItem(`eternavaults_passcode_${targetId}`) || passcode;
    
    console.log('🔐 PasscodeEntry (MFA): About to store passcode for:', targetId);
    console.log('🔐 PasscodeEntry (MFA): Passcode from sessionStorage:', storedPasscode ? 'Found' : 'Not found');
    console.log('🔐 PasscodeEntry (MFA): Passcode from state:', passcode ? 'Has passcode' : 'No passcode');
    
    if (storedPasscode) {
      // Store directly in sessionStorage (already stored, just update timestamp)
      sessionStorage.setItem(`eternavaults_passcode_${targetId}`, storedPasscode);
      sessionStorage.setItem(`eternavaults_passcode_timestamp_${targetId}`, Date.now().toString());
      console.log('✅ PasscodeEntry (MFA): Passcode confirmed in sessionStorage');
      const verifyPasscode = sessionStorage.getItem(`eternavaults_passcode_${targetId}`);
      console.log('🔍 PasscodeEntry (MFA): Verification - passcode found?', !!verifyPasscode);
    } else {
      console.warn('⚠️ PasscodeEntry (MFA): No passcode found in sessionStorage or state!');
    }
    
    // Success animation before redirect
    gsap.to(containerRef.current, {
      scale: 1.05,
      duration: 0.3,
      ease: "power3.out",
      onComplete: () => {
        const targetId = member._id || member.id || member.memberId;
        navigate(`/dashboard/${targetId}`);
      }
    });
  };
  
  const handleMFAClose = () => {
    setShowMFA(false);
    setMfaData(null);
    setPasscode('');
  };
  
  // Handle paste event to ensure full paste works
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const numericOnly = pastedText.replace(/[^0-9]/g, '');
    setPasscode(numericOnly);
    // Focus and move cursor to end after paste
    setTimeout(() => {
      if (passcodeRef.current) {
        passcodeRef.current.setSelectionRange(numericOnly.length, numericOnly.length);
      }
    }, 0);
  };

  return (
    <div className="passcode-entry-bg" ref={containerRef}>
      <div className="passcode-entry-container">
        <button className="back-btn" onClick={handleBack}>← Back</button>
        
        <h1 className="passcode-title">ETERNA VAULTS</h1>
        <p className="passcode-subtitle">Enter Passcode for {member?.name}</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            className="passcode-input"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, ''))}
            onPaste={handlePaste}
            ref={passcodeRef}
            autoFocus
            placeholder="Enter your passcode"
            inputMode="numeric"
          />
          
          {error && <div className="passcode-error">{error}</div>}
          
          <button 
            type="submit" 
            className="passcode-btn"
            disabled={passcode.length < 4 || loading}
            title={passcode.length < 4 ? 'Passcode must be at least 4 digits' : ''}
          >
            {loading ? 'Verifying...' : 'Enter Vault'}
          </button>
          
          <div className="passcode-links">
            <button 
              type="button" 
              className="text-link"
              onClick={handleForgotPasscode}
            >
              Forgot Passcode?
            </button>
          </div>
        </form>
      </div>
      
      {showMFA && mfaData && (
        <MFAVerification
          member={member}
          deviceId={mfaData.deviceId}
          deviceName={mfaData.deviceName}
          onSuccess={handleMFAVerified}
          onCancel={handleMFAClose}
        />
      )}
    </div>
  );
}
